import { NuxtLink, PagesBlocksServices, PagesBlocksEmployees, PagesBlocksPasses, PagesBlocksHero, UButton } from "#components"
import type { Render } from "@/types/plugins"

const controller = {
  process (data) {
    if (this[data.type]) {
      return this[data.type](data)
    }
    
    return h('p', { class: 'text-red-500' }, data.type)
  },

  content (content = [], html = []) {
    content.forEach((item) => {
      html.push(this.process(item))
    })

    return html
  },

  doc ({ content }) {
    const html = []

    html.push(h('div', { class: 'doc' }, this.content(content)))

    return html
  },

  text ({ marks = [], text }) {
    let html = text

    for (const mark of marks) {
      if (this.marks[mark.type]) {
        html = this.marks[mark.type](html, mark)
      }
    }

    return html
  },

  hardBreak () {
    return h('br')
  },

  marks: {
    bold (content) {
      return h('strong', content)
    },
    italic (content) {
      return h('em', content)
    },
    strike (content) {
      return h('s', content)
    },
    link (content, mark) {
      if (mark.attrs.element === 'link') {
        if (mark.attrs.type === 'internal') {
          return h(NuxtLink, { class: 'text-primary underline', to: mark.attrs.href, target: mark.attrs.target }, content)
        }

        if (mark.attrs.type === 'external') {
          return h('a', { class: 'text-primary underline', href: mark.attrs.href, target: mark.attrs.target }, content)
        }
      }

      if (mark.attrs.element === 'button') {
        if (mark.attrs.type === 'internal') {
          return h(UButton, { to: mark.attrs.href, size: 'lg' }, content)
        }

        if (mark.attrs.type === 'external') {
          return h(UButton, { to: mark.attrs.href, size: 'lg', external: true }, content)
        }
      }
    }
  },

  heading ({ attrs, content }) {
    return h(`h${attrs.level}`, { class: `text-${attrs.textAlign}` }, this.content(content))
  },

  paragraph ({ attrs, content }) {
    return h('p', { class: `text-${attrs.textAlign}` }, this.content(content))
  },

  image ({ attrs }) {
    return h('img', { src: attrs.src, class: 'rounded my-6 mx-auto' })
  },

  columns ({ content }) {
    const cols = content
      .reduce((acc, item) => {
        if (item.type === 'column') {
          return acc + item.attrs.span
        }

        return acc
      }, 0)

    return h('div', { class: `grid grid-cols-1 sm:gap-4 sm:grid-cols-${cols}` }, this.content(content))
  },

  column ({ attrs, content }) {
    return h('div', { class: `col-span-${attrs.span} break-words` }, this.content(content))
  },

  bulletList ({ content }) {
    return h('ul', { class: 'list-disc list-inside' }, this.content(content))
  },

  orderedList ({ content }) {
    return h('ol', { class: 'list-decimal list-inside' }, this.content(content))
  },

  listItem ({ content }) {
    return h('li', this.content(content))
  },

  table ({ content }) {
    const html = h('table', { class: 'w-full text-left text-sm text-secondary mb-4 table-fixed' }, [
      h('colgroup', this.content(content[0].content)),
      h('tbody', this.content(content.slice(1)))
    ])

    return html
  },

  tableRow ({ content }) {
    return h('tr', this.content(content))
  },

  tableHeader ({ attrs, content }) {
    return h('th', { class: 'text-xs text-primary uppercase border-b p-3 bg-action/10', colspan: attrs.colspan, rowspan: attrs.rowspan, colwidth: attrs.colwidth }, this.content(content))
  },

  tableCell ({ attrs, content }) {
    return h('td', { class: 'border-b p-3 break-words', colspan: attrs.colspan, rowspan: attrs.rowspan, colwidth: attrs.colwidth }, this.content(content))
  },

  hero ({ attrs }) {
    return h(PagesBlocksHero, { title: attrs.title, info: attrs.info, image: attrs.image })
  },

  services ({ attrs }) {
    return h(PagesBlocksServices, { title: attrs.title, info: attrs.info, services: attrs.services, images: attrs.images })
  },

  employees ({ attrs }) {
    return h(PagesBlocksEmployees, { title: attrs.title, info: attrs.info, employees: attrs.employees, images: attrs.images })
  },

  passes ({ attrs }) {
    return h(PagesBlocksPasses, { title: attrs.title, info: attrs.info, passes: attrs.passes, images: attrs.images })
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const render: Render = {
    object: (data) => {
      return controller.process(data)
    },
  }

  nuxtApp.provide('render', render)
})
